import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { whatsappWebService, WhatsAppWebSession } from '../services/whatsappWebService';

// Define session type with additional metadata
interface SessionWithMeta extends WhatsAppWebSession {
  name: string;
  lastActive?: Date;
}

const WhatsAppWebSessions: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  
  const [sessions, setSessions] = useState<SessionWithMeta[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Mock function to get sessions from local storage
  // In a real implementation, this would come from the backend API
  const getSessions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // In a real implementation, we would fetch this from the backend
      // For now, we'll use localStorage to store session metadata
      const sessionData = localStorage.getItem('whatsappWebSessions');
      let userSessions: Record<string, { name: string, lastActive?: string }> = {};
      
      if (sessionData) {
        userSessions = JSON.parse(sessionData);
      }
      
      // Get status for each session
      const sessionPromises = Object.entries(userSessions)
        .filter(([key]) => key.startsWith(`${user?.id}_`))
        .map(async ([key, meta]) => {
          try {
            const sessionId = key.split('_')[1];
            const status = await whatsappWebService.getSessionStatus(key);
            
            return {
              ...status,
              name: meta.name,
              lastActive: meta.lastActive ? new Date(meta.lastActive) : undefined,
              sessionId
            };
          } catch (err) {
            // Session might not exist on the server anymore
            return null;
          }
        });
      
      const results = await Promise.all(sessionPromises);
      setSessions(results.filter(Boolean) as SessionWithMeta[]);
      
    } catch (err: any) {
      setError(err.message || 'Failed to load WhatsApp Web sessions');
      console.error('Error loading sessions:', err);
    } finally {
      setLoading(false);
    }
  }, [user]); // Add user as a dependency
  
  // Load sessions on component mount
  useEffect(() => {
    if (user) {
      getSessions();
    }
  }, [user, getSessions]);
  
  // Delete session
  const deleteSession = async (sessionKey: string, sessionId: string) => {
    if (window.confirm('Are you sure you want to delete this WhatsApp Web session?')) {
      try {
        await whatsappWebService.deleteSession(sessionKey);
        
        // Update local storage
        const sessionData = localStorage.getItem('whatsappWebSessions');
        if (sessionData) {
          const userSessions = JSON.parse(sessionData);
          delete userSessions[sessionKey];
          localStorage.setItem('whatsappWebSessions', JSON.stringify(userSessions));
        }
        
        // Refresh sessions list
        getSessions();
        
      } catch (err: any) {
        setError(err.message || 'Error deleting session');
        console.error('Error deleting session:', err);
      }
    }
  };
  
  // Render status badge
  const renderStatusBadge = (status: string) => {
    let bgColor = 'bg-gray-100';
    let textColor = 'text-gray-800';
    
    switch (status) {
      case 'ready':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'authenticated':
        bgColor = 'bg-blue-100';
        textColor = 'text-blue-800';
        break;
      case 'qr_received':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      case 'disconnected':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
      case 'auth_failure':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
      case 'initializing':
        bgColor = 'bg-purple-100';
        textColor = 'text-purple-800';
        break;
    }
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>
    );
  };

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <p className="text-yellow-700">
            You need to be logged in to view WhatsApp Web sessions.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">WhatsApp Web Sessions</h1>
        <button
          onClick={() => navigate('/whatsapp-web/new')}
          className="px-4 py-2 bg-whatsapp-green text-white rounded hover:bg-whatsapp-dark-green"
        >
          New Session
        </button>
      </div>
      
      {error && (
        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded">
          <p className="text-red-700">{error}</p>
        </div>
      )}
      
      {loading ? (
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-whatsapp-green"></div>
        </div>
      ) : sessions.length > 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {sessions.map((session) => (
              <li key={session.sessionKey}>
                <Link 
                  to={`/whatsapp-web/${session.name}`}
                  className="block hover:bg-gray-50"
                >
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <svg className="h-10 w-10 text-whatsapp-green" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {session.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {session.lastActive ? (
                              <span>Last active: {session.lastActive.toLocaleString()}</span>
                            ) : (
                              <span>Never used</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        {renderStatusBadge(session.status)}
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            deleteSession(session.sessionKey, session.name);
                          }}
                          className="ml-4 text-red-600 hover:text-red-900"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No WhatsApp Web sessions</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new WhatsApp Web session.
            </p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/whatsapp-web/new')}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-whatsapp-green hover:bg-whatsapp-dark-green focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-whatsapp-green"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                New Session
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="mt-8 bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4">About WhatsApp Web Integration</h2>
        <p className="text-gray-700 mb-4">
          This feature allows you to connect your WhatsApp account to our system using the WhatsApp Web protocol.
          This is an alternative to the WhatsApp Business API and has different capabilities and limitations.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium text-lg mb-2">Advantages</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>No approval process required</li>
              <li>Works with regular WhatsApp accounts</li>
              <li>No monthly fees from Meta/WhatsApp</li>
              <li>Quick setup with QR code scanning</li>
            </ul>
          </div>
          
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium text-lg mb-2">Limitations</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>No message templates support</li>
              <li>Limited to 4 connected devices</li>
              <li>Phone must connect to internet periodically</li>
              <li>Not officially supported for business use</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppWebSessions;
